<template>
    <v-dialog :value="true" max-width="1100" scrollable content-class="sa-detail-dialog" :fullscreen="$vuetify.breakpoint.smAndDown" @input="close">
        <v-card class="sa-detail">
            <div v-if="!data" class="pa-12 text-center">
                <v-progress-circular indeterminate color="#2a78d6" />
            </div>

            <template v-else>
                <!-- Header -->
                <div class="sa-detail__header">
                    <div class="d-flex align-start">
                        <div class="flex-grow-1">
                            <div class="text-h5 font-weight-bold">{{ a.name }}</div>
                            <div class="text-body-2 grey--text text--darken-1">
                                {{ a.email }}
                                <span v-if="a.submitted_at"> · Submitted {{ formatDate(a.submitted_at, true) }}</span>
                                <span v-if="a.duration_seconds"> · {{ Math.round(a.duration_seconds / 60) }} min</span>
                                <span v-if="a.source"> · Source: {{ a.source }}</span>
                            </div>
                            <div v-if="data.previous.length" class="text-caption mt-1">
                                <v-icon x-small>mdi-history</v-icon>
                                {{ data.previous.length }} other attempt{{ data.previous.length > 1 ? 's' : '' }}:
                                <span v-for="(p, i) in data.previous" :key="p.id">
                                    <a @click="switchTo(p.id)">
                                        {{ formatDate(p.submitted_at || p.created_at) }}
                                        ({{ p.overall_score === null ? 'unfinished' : Math.round(p.overall_score) }})</a>{{ i < data.previous.length - 1 ? ', ' : '' }}
                                </span>
                            </div>
                        </div>
                        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
                    </div>

                    <div class="sa-detail__summary">
                        <div class="sa-hero">
                            <div class="sa-hero__label">Overall score</div>
                            <div class="sa-hero__value">
                                {{ a.overall_score === null ? '–' : Math.round(a.overall_score) }}<span>/100</span>
                            </div>
                            <span v-if="band" class="sa-band" :style="{ background: band.tint }">
                                <v-icon x-small :color="band.color" class="mr-1">{{ band.icon }}</v-icon>{{ band.label }}
                            </span>
                        </div>

                        <div class="sa-hero">
                            <div class="sa-hero__label">Answer consistency</div>
                            <div class="sa-hero__value sa-hero__value--small">
                                {{ a.consistency_score === null ? '–' : a.consistency_score }}<span>/100</span>
                            </div>
                            <div class="text-caption grey--text text--darken-1">
                                {{ a.consistency_score >= 60 ? 'Answers look sincere' : 'Interpret with caution' }}
                            </div>
                        </div>

                        <div class="sa-hero">
                            <div class="sa-hero__label">Flags</div>
                            <div class="sa-hero__value sa-hero__value--small">{{ (a.flags || []).length }}</div>
                            <div class="text-caption grey--text text--darken-1">
                                <template v-if="(a.flags || []).length">
                                    <span v-for="lvl in flagLevelCounts" :key="lvl.level" class="mr-2">
                                        <v-icon x-small :color="FLAG_LEVELS[lvl.level].color">{{ FLAG_LEVELS[lvl.level].icon }}</v-icon>
                                        {{ lvl.count }} {{ FLAG_LEVELS[lvl.level].label.toLowerCase() }}
                                    </span>
                                </template>
                                <template v-else>None raised</template>
                            </div>
                        </div>

                        <div class="sa-ai-state">
                            <template v-if="a.status === 'evaluating'">
                                <v-progress-circular indeterminate size="16" width="2" color="#2a78d6" class="mr-2" />
                                AI is reading the written answers…
                            </template>
                            <template v-else-if="a.status === 'ai_failed'">
                                <v-icon small color="#d03b3b" class="mr-1">mdi-alert-octagon</v-icon>
                                <span>AI evaluation failed. Scores are provisional (multiple choice only).</span>
                                <v-btn small text color="#2a78d6" class="text-none ml-1" :loading="busy.reevaluate"
                                    @click="reevaluate">Try again</v-btn>
                            </template>
                            <template v-else-if="a.status === 'completed'">
                                <v-icon small color="#0ca30c" class="mr-1">mdi-check-circle</v-icon>
                                Evaluated {{ formatDate(a.evaluated_at, true) }}
                                <span v-if="Object.keys(a.overrides || {}).length" class="ml-1">
                                    · {{ Object.keys(a.overrides).length }} score(s) changed by a reviewer
                                </span>
                            </template>
                        </div>
                    </div>

                    <div v-if="aiFlag" class="sa-ai-warning">
                        <v-icon small color="#d03b3b" class="mr-2">mdi-robot-outline</v-icon>
                        <div><strong>Likely AI-written.</strong> {{ aiFlag.message }}</div>
                    </div>
                </div>

                <v-tabs v-model="tab" color="#1a237e" class="px-4" show-arrows>
                    <v-tab class="text-none">Profile</v-tab>
                    <v-tab class="text-none">Flags ({{ sortedFlags.length }})</v-tab>
                    <v-tab class="text-none">Written answers</v-tab>
                    <v-tab class="text-none">Scenarios</v-tab>
                    <v-tab class="text-none">Statements &amp; pairs</v-tab>
                    <v-tab class="text-none">Review</v-tab>
                </v-tabs>
                <v-divider />

                <v-card-text class="sa-detail__body">
                    <v-tabs-items v-model="tab">
                        <!-- Profile -->
                        <v-tab-item>
                            <v-row>
                                <v-col cols="12" md="5">
                                    <h3 class="sa-h3">Category scores</h3>
                                    <div v-for="cat in CATEGORY_ORDER" :key="cat" class="sa-cat">
                                        <div class="sa-cat__label">
                                            {{ data.categories[cat].label }}
                                            <span class="sa-cat__weight">{{ data.weights[cat] }}%</span>
                                        </div>
                                        <score-bar :value="catScore(cat)" :tooltip="catTooltip(cat)" />
                                    </div>
                                    <div class="text-caption grey--text mt-1">
                                        Hover a bar to see how it was built. Percent = weight in the overall score.
                                    </div>

                                    <h3 class="sa-h3 mt-6">About them <span class="sa-h3__note">(not scored)</span></h3>
                                    <div v-for="item in data.breakdown.background" :key="item.id" class="sa-fact">
                                        <div class="text-caption grey--text text--darken-1">{{ backgroundLabel(item.id) }}</div>
                                        <div class="text-body-2">{{ item.answer || '–' }}</div>
                                    </div>
                                </v-col>

                                <v-col cols="12" md="7">
                                    <template v-if="summary">
                                        <h3 class="sa-h3">AI candidate profile</h3>
                                        <p class="sa-profile">{{ summary.profile }}</p>
                                        <div class="sa-note">
                                            <v-icon small color="#1a237e" class="mr-2">mdi-lightbulb-on-outline</v-icon>
                                            <span>{{ summary.recommendation_note }}</span>
                                        </div>

                                        <h3 class="sa-h3 mt-5">Strengths</h3>
                                        <div v-for="(s, i) in summary.strengths" :key="'s' + i" class="sa-point">
                                            <v-icon small color="#0ca30c" class="mr-2">mdi-plus-circle</v-icon>
                                            <div><strong>{{ s.title }}</strong> {{ s.detail }}</div>
                                        </div>

                                        <h3 class="sa-h3 mt-5">Development areas</h3>
                                        <div v-for="(d, i) in summary.development_areas" :key="'d' + i" class="sa-point">
                                            <v-icon small color="#c98a00" class="mr-2">mdi-arrow-up-circle</v-icon>
                                            <div><strong>{{ d.title }}</strong> {{ d.detail }}</div>
                                        </div>

                                        <h3 class="sa-h3 mt-5">Suggested interview questions</h3>
                                        <ol class="sa-questions">
                                            <li v-for="(q, i) in summary.interview_questions" :key="'q' + i">{{ q }}</li>
                                        </ol>

                                        <div v-if="summaryStale" class="text-caption grey--text mt-3">
                                            Written before a reviewer changed a score; the numbers on the left are current.
                                        </div>
                                        <div class="text-caption grey--text mt-3">
                                            Written by AI ({{ a.ai_model }}) to support, not replace, your judgement.
                                        </div>
                                    </template>
                                    <div v-else class="sa-empty">
                                        <v-icon large color="grey lighten-1">mdi-text-box-search-outline</v-icon>
                                        <div class="mt-2">
                                            {{ a.status === 'evaluating' ? 'The AI profile will appear here in a minute or two.' : 'No AI profile yet.' }}
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-tab-item>

                        <!-- Flags -->
                        <v-tab-item>
                            <div v-if="!sortedFlags.length" class="text-body-2 grey--text">No flags raised.</div>
                            <div v-else class="sa-flags">
                                <div v-for="(flag, i) in sortedFlags" :key="i" class="sa-flag">
                                    <v-icon small :color="FLAG_LEVELS[flag.level].color" class="mr-2">
                                        {{ FLAG_LEVELS[flag.level].icon }}
                                    </v-icon>
                                    <div>
                                        <strong>{{ FLAG_SHORT[flag.code] || flag.code }}</strong>
                                        <span class="sa-flag__level">· {{ FLAG_LEVELS[flag.level].label }}</span>
                                        <div class="text-body-2">{{ flag.message }}</div>
                                    </div>
                                </div>
                            </div>
                        </v-tab-item>

                        <!-- Written answers -->
                        <v-tab-item>
                            <div v-for="w in data.breakdown.written" :key="w.id" class="sa-written">
                                <div class="sa-written__q">
                                    <span class="sa-tag">{{ w.id }}</span> {{ w.text }}
                                </div>
                                <div class="text-caption grey--text text--darken-1 mb-2">
                                    Scores: {{ w.categories.join(', ') }} + Communication · {{ w.words }} words
                                    <span v-if="w.pasted" class="ml-1">
                                        · <v-icon x-small color="#c98a00">mdi-content-paste</v-icon> text was pasted
                                    </span>
                                </div>
                                <blockquote class="sa-answer">{{ w.answer }}</blockquote>

                                <div v-if="w.ai" class="sa-ai">
                                    <div class="d-flex flex-wrap align-center" style="gap: 16px">
                                        <div class="sa-ai__score">
                                            <div class="text-caption grey--text text--darken-1">Trait score</div>
                                            <div>
                                                <span :class="{ 'sa-struck': w.override }">{{ w.ai.trait_score }}/5</span>
                                                <strong v-if="w.override" class="ml-1">{{ w.override.score }}/5</strong>
                                            </div>
                                        </div>
                                        <div class="sa-ai__score">
                                            <div class="text-caption grey--text text--darken-1">Communication</div>
                                            <div>{{ w.ai.communication }}/5</div>
                                        </div>
                                        <div class="sa-ai__score">
                                            <div class="text-caption grey--text text--darken-1">Reasoning</div>
                                            <div>{{ w.ai.reasoning }}/5</div>
                                        </div>
                                        <div class="sa-ai__score">
                                            <div class="text-caption grey--text text--darken-1">AI confidence</div>
                                            <div class="text-capitalize">{{ w.ai.confidence }}</div>
                                        </div>
                                        <v-spacer />
                                        <v-btn small outlined class="text-none" @click="startOverride(w)">
                                            <v-icon left small>mdi-pencil</v-icon>
                                            {{ w.override ? 'Change override' : 'Override score' }}
                                        </v-btn>
                                    </div>
                                    <div class="text-body-2 mt-2">{{ w.ai.rationale }}</div>
                                    <div v-if="w.ai.evidence_quotes.length" class="mt-2">
                                        <span v-for="(q, i) in w.ai.evidence_quotes" :key="i" class="sa-quote">“{{ q }}”</span>
                                    </div>
                                    <div v-if="w.ai.flags.length" class="mt-2">
                                        <span v-for="f in w.ai.flags" :key="f" class="sa-aiflag">
                                            <v-icon x-small color="#c98a00" class="mr-1">mdi-alert</v-icon>{{ aiFlagLabel(f) }}
                                        </span>
                                    </div>
                                    <div v-if="w.override" class="sa-override">
                                        <v-icon x-small class="mr-1">mdi-account-edit</v-icon>
                                        Changed from {{ w.override.ai_score }} to {{ w.override.score }} by {{ w.override.by }}
                                        on {{ formatDate(w.override.at, true) }}: “{{ w.override.reason }}”
                                    </div>

                                    <div v-if="override.id === w.id" class="sa-override-form">
                                        <div class="d-flex flex-wrap align-start" style="gap: 12px">
                                            <v-select v-model="override.score" :items="[1, 2, 3, 4, 5]" label="New score"
                                                dense outlined hide-details style="max-width: 120px" />
                                            <v-text-field v-model="override.reason" label="Reason (required)" dense
                                                outlined hide-details class="flex-grow-1" style="min-width: 220px" />
                                        </div>
                                        <div class="d-flex mt-2" style="gap: 8px">
                                            <v-btn small depressed color="#1a237e" class="text-none"
                                                :dark="!!(override.score && override.reason)"
                                                :loading="busy.override" :disabled="!override.score || !override.reason"
                                                @click="saveOverride">Save</v-btn>
                                            <v-btn v-if="w.override" small text class="text-none" :loading="busy.override"
                                                @click="clearOverride(w)">Restore AI score</v-btn>
                                            <v-btn small text class="text-none" @click="override.id = null">Cancel</v-btn>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-body-2 grey--text">Not yet scored by AI.</div>
                            </div>
                        </v-tab-item>

                        <!-- Scenarios -->
                        <v-tab-item>
                            <div v-for="s in data.breakdown.scenarios" :key="s.id" class="sa-scenario">
                                <div class="d-flex align-start">
                                    <div class="flex-grow-1">
                                        <div class="text-caption grey--text text--darken-1">{{ s.category }}</div>
                                        <div class="font-weight-medium mb-1">{{ s.text }}</div>
                                        <div class="text-body-2">
                                            <v-icon x-small :color="s.points === 3 ? '#0ca30c' : '#8a90a6'" class="mr-1">
                                                {{ s.points === 3 ? 'mdi-check-circle' : 'mdi-circle-medium' }}
                                            </v-icon>
                                            Chose: {{ s.chosen }}
                                        </div>
                                        <div v-if="s.points !== 3" class="text-caption grey--text text--darken-1 mt-1">
                                            Strongest option: {{ s.best }}
                                        </div>
                                    </div>
                                    <div class="sa-points-pill">{{ s.points }}/{{ s.max }}</div>
                                </div>
                            </div>
                        </v-tab-item>

                        <!-- Statements & pairs -->
                        <v-tab-item>
                            <h3 class="sa-h3">Agree or disagree</h3>
                            <v-simple-table dense class="mb-6">
                                <thead>
                                    <tr>
                                        <th>Statement</th>
                                        <th>Category</th>
                                        <th>Answer</th>
                                        <th class="text-right">Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="l in data.breakdown.likert" :key="l.id">
                                        <td class="py-1">
                                            {{ l.text }}
                                            <span v-if="l.reverse" class="sa-rev" title="Reverse scored: agreeing lowers the score">R</span>
                                        </td>
                                        <td class="text-caption">{{ l.category }}</td>
                                        <td class="text-no-wrap">{{ l.answer || '–' }}</td>
                                        <td class="text-right text-no-wrap">
                                            {{ l.max ? l.points + '/' + l.max : 'not scored' }}
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                            <h3 class="sa-h3">Most like me</h3>
                            <v-simple-table dense>
                                <thead>
                                    <tr>
                                        <th>Chose</th>
                                        <th>Over</th>
                                        <th>Category</th>
                                        <th class="text-right">Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="f in data.breakdown.forced" :key="f.id">
                                        <td class="py-1">{{ f.chosen }}</td>
                                        <td class="py-1 grey--text text--darken-1">{{ f.other }}</td>
                                        <td class="text-caption">{{ f.category }}</td>
                                        <td class="text-right">{{ f.keyed ? 1 : 0 }}/1</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-tab-item>

                        <!-- Review -->
                        <v-tab-item>
                            <v-row>
                                <v-col cols="12" md="7">
                                    <h3 class="sa-h3">Your review</h3>
                                    <v-select v-model="review.review_status" :items="REVIEW_STATUSES" label="Status"
                                        outlined dense />
                                    <v-textarea v-model="review.review_notes" label="Notes (interview impressions, next steps)"
                                        outlined auto-grow rows="5" />
                                    <div class="d-flex align-center">
                                        <v-btn depressed color="#1a237e" dark class="text-none" :loading="busy.review"
                                            @click="saveReview">Save review</v-btn>
                                        <span v-if="a.reviewed_at" class="text-caption grey--text ml-3">
                                            Last updated by {{ a.reviewer ? a.reviewer.name : 'someone' }},
                                            {{ formatDate(a.reviewed_at, true) }}
                                        </span>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="5">
                                    <h3 class="sa-h3">Actions</h3>
                                    <v-btn outlined block class="text-none mb-3" :loading="busy.reevaluate"
                                        :disabled="a.status === 'evaluating'" @click="reevaluate">
                                        <v-icon left small>mdi-robot-outline</v-icon> Re-run AI evaluation
                                    </v-btn>
                                    <div class="text-caption grey--text mb-6">
                                        Asks the AI to score the written answers and write the profile again. Your score
                                        overrides are kept.
                                    </div>
                                    <v-btn outlined block color="#d03b3b" class="text-none" @click="confirmDelete = true">
                                        <v-icon left small>mdi-delete-outline</v-icon> Delete permanently
                                    </v-btn>
                                    <div class="text-caption grey--text mt-1">
                                        For data deletion requests. This cannot be undone.
                                    </div>
                                </v-col>
                            </v-row>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
            </template>
        </v-card>

        <v-dialog v-model="confirmDelete" max-width="420">
            <v-card>
                <v-card-title class="text-h6">Delete this assessment?</v-card-title>
                <v-card-text>
                    All of {{ a ? a.name : 'this candidate' }}'s answers, scores and notes will be permanently deleted.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text class="text-none" @click="confirmDelete = false">Cancel</v-btn>
                    <v-btn depressed color="#d03b3b" dark class="text-none" :loading="busy.delete" @click="destroy">
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import ScoreBar from './ScoreBar.vue'
import { BANDS, FLAG_LEVELS, FLAG_SHORT, REVIEW_STATUSES, CATEGORY_ORDER } from './assessmentUi'

const AI_FLAG_LABELS = {
    possibly_generic_or_ai_written: 'Possibly generic or AI-written',
    off_topic: 'Off topic',
    too_short_to_judge: 'Too short to judge',
    concerning_content: 'Concerning content',
}

const PART_LABELS = { likert: 'Agree/disagree', forced: 'Most like me', scenario: 'Scenarios', written: 'Written (AI)' }

export default {
    name: 'SalesAssessmentDetail',
    components: { ScoreBar },
    props: {
        assessmentId: { type: Number, required: true },
    },
    data() {
        return {
            FLAG_LEVELS, FLAG_SHORT, REVIEW_STATUSES, CATEGORY_ORDER,
            currentId: this.assessmentId,
            data: null,
            tab: 0,
            review: { review_status: 'new', review_notes: '' },
            override: { id: null, score: null, reason: '' },
            busy: { review: false, override: false, reevaluate: false, delete: false },
            confirmDelete: false,
            pollTimer: null,
        }
    },
    computed: {
        a() {
            return this.data ? this.data.assessment : null
        },
        band() {
            return this.a && BANDS[this.a.band]
        },
        aiFlag() {
            return this.a && (this.a.flags || []).find(f => f.code === 'ai_written')
        },
        sortedFlags() {
            const order = ['red', 'amber', 'info']
            return [...((this.a && this.a.flags) || [])].sort((x, y) => order.indexOf(x.level) - order.indexOf(y.level))
        },
        flagLevelCounts() {
            return ['red', 'amber', 'info']
                .map(level => ({ level, count: this.sortedFlags.filter(f => f.level === level).length }))
                .filter(l => l.count)
        },
        summary() {
            return this.a && this.a.ai_summary
        },
        summaryStale() {
            const overrides = Object.values((this.a && this.a.overrides) || {})
            if (!this.a || !this.a.evaluated_at) return false
            const evaluated = new Date(this.a.evaluated_at).getTime()
            return overrides.some(o => new Date(o.at).getTime() > evaluated)
        },
    },
    created() {
        this.load()
    },
    beforeDestroy() {
        clearTimeout(this.pollTimer)
    },
    methods: {
        async load() {
            const res = await this.$axios.get(`api/sales-assessments/${this.currentId}`)
            if (!res.data || !res.data.response) {
                this.$toast.error((res.data && res.data.message) || 'Could not load assessment')
                this.$emit('close')
                return
            }
            const wasEvaluating = this.a && this.a.status === 'evaluating'
            this.data = res.data
            this.review = { review_status: this.a.review_status, review_notes: this.a.review_notes || '' }

            clearTimeout(this.pollTimer)
            if (this.a.status === 'evaluating') {
                this.pollTimer = setTimeout(() => this.load(), 8000)
            } else if (wasEvaluating) {
                this.$emit('changed')
            }
        },
        switchTo(id) {
            this.currentId = id
            this.data = null
            this.tab = 0
            this.load()
        },
        close() {
            this.$emit('close')
        },
        catScore(cat) {
            const c = this.a.category_scores && this.a.category_scores[cat]
            return c ? c.score : null
        },
        catTooltip(cat) {
            const c = this.a.category_scores && this.a.category_scores[cat]
            if (!c) return ''
            const parts = Object.entries(c.parts || {}).map(([k, v]) => `${PART_LABELS[k] || k}: ${Math.round(v)}`)
            return `${this.data.categories[cat].description} ${parts.length ? '— ' + parts.join(' · ') : '— no data yet'}`
        },
        backgroundLabel(id) {
            return { evening_availability: 'Evening shifts', commute: 'Commute (one way)', experience: 'Customer-facing experience' }[id] || id
        },
        aiFlagLabel(flag) {
            return AI_FLAG_LABELS[flag] || flag
        },
        formatDate(value, withTime = false) {
            return value ? this.$date(value).format(withTime ? 'D MMM YYYY, HH:mm' : 'D MMM YYYY') : ''
        },
        startOverride(w) {
            this.override = { id: w.id, score: w.override ? w.override.score : w.ai.trait_score, reason: w.override ? w.override.reason : '' }
        },
        async saveOverride() {
            await this.postOverride({ question_id: this.override.id, score: this.override.score, reason: this.override.reason })
        },
        async clearOverride(w) {
            await this.postOverride({ question_id: w.id, score: null, reason: null })
        },
        async postOverride(payload) {
            this.busy.override = true
            const res = await this.$axios.post(`api/sales-assessments/${this.currentId}/override`, payload)
            this.busy.override = false
            if (!res.data || !res.data.response) {
                this.$toast.error((res.data && res.data.message) || 'Could not save the score')
                return
            }
            this.data = res.data
            this.override = { id: null, score: null, reason: '' }
            this.$toast.success('Score updated')
            this.$emit('changed')
        },
        async saveReview() {
            this.busy.review = true
            const res = await this.$axios.post(`api/sales-assessments/${this.currentId}`, this.review)
            this.busy.review = false
            if (!res.data || !res.data.response) {
                this.$toast.error((res.data && res.data.message) || 'Could not save the review')
                return
            }
            this.data.assessment = { ...this.data.assessment, ...res.data.assessment }
            this.$toast.success('Review saved')
            this.$emit('changed')
        },
        async reevaluate() {
            this.busy.reevaluate = true
            const res = await this.$axios.post(`api/sales-assessments/${this.currentId}/reevaluate`, {})
            this.busy.reevaluate = false
            if (!res.data || !res.data.response) {
                this.$toast.error((res.data && res.data.message) || 'Could not start the evaluation')
                return
            }
            this.$toast.info('AI evaluation started. This usually takes a minute or two.')
            this.$emit('changed')
            this.load()
        },
        async destroy() {
            this.busy.delete = true
            try {
                await this.$axios.delete(`api/sales-assessments/${this.currentId}`)
                this.$toast.success('Assessment deleted')
                this.confirmDelete = false
                this.$emit('deleted')
            } catch (e) {
                this.$toast.error('Could not delete the assessment')
            } finally {
                this.busy.delete = false
            }
        },
    },
}
</script>

<style scoped>
.sa-detail__header {
    padding: 20px 24px 12px;
}

.sa-detail__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 16px;
    margin-top: 16px;
}

.sa-hero {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    padding: 10px 18px;
    min-width: 170px;
}

.sa-hero__label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.sa-hero__value {
    font-size: 40px;
    font-weight: 600;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    color: rgba(0, 0, 0, 0.87);
}

.sa-hero__value--small {
    font-size: 30px;
}

.sa-hero__value span {
    font-size: 15px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.5);
    margin-left: 2px;
}

.sa-ai-state {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
    flex: 1;
    min-width: 240px;
}

.sa-band {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
    margin-top: 4px;
}

.sa-detail__body {
    padding-top: 20px !important;
    min-height: 420px;
}

.sa-h3 {
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 10px;
    color: rgba(0, 0, 0, 0.87);
}

.sa-h3__note {
    font-weight: 400;
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
}

.sa-cat {
    margin-bottom: 6px;
}

.sa-cat__label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: space-between;
}

.sa-cat__weight {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}

.sa-ai-warning {
    display: flex;
    align-items: flex-start;
    margin-top: 12px;
    padding: 10px 14px;
    border-radius: 8px;
    background: #fdecec;
    color: #8a1f1f;
    font-size: 14px;
}

.sa-flag,
.sa-point {
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.85);
}

.sa-flags {
    margin-bottom: 20px;
    padding: 14px 16px 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
}

.sa-flag__level {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.sa-fact {
    margin-bottom: 8px;
}

.sa-profile {
    font-size: 15px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.85);
}

.sa-note {
    display: flex;
    align-items: flex-start;
    background: #eef1fb;
    border-radius: 10px;
    padding: 12px 14px;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.85);
}

.sa-questions {
    padding-left: 20px;
    line-height: 1.55;
    color: rgba(0, 0, 0, 0.85);
}

.sa-questions li {
    margin-bottom: 6px;
}

.sa-empty {
    text-align: center;
    padding: 60px 20px;
    color: rgba(0, 0, 0, 0.5);
}

.sa-written,
.sa-scenario {
    padding: 16px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.sa-written__q {
    font-weight: 600;
    font-size: 15px;
    margin-bottom: 4px;
    color: rgba(0, 0, 0, 0.87);
}

.sa-tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    color: #1a237e;
    background: #e8eaf6;
    border-radius: 4px;
    padding: 1px 6px;
    margin-right: 4px;
}

.sa-answer {
    white-space: pre-wrap;
    background: #fafafa;
    border-left: 3px solid #c5cae9;
    padding: 10px 14px;
    margin: 0 0 12px;
    line-height: 1.55;
    color: rgba(0, 0, 0, 0.85);
}

.sa-ai {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    padding: 12px 14px;
}

.sa-ai__score {
    min-width: 90px;
    font-variant-numeric: tabular-nums;
}

.sa-struck {
    text-decoration: line-through;
    color: rgba(0, 0, 0, 0.45);
}

.sa-quote {
    display: inline-block;
    font-size: 13px;
    font-style: italic;
    background: #f4f6fb;
    border-radius: 6px;
    padding: 2px 8px;
    margin: 0 6px 6px 0;
    color: rgba(0, 0, 0, 0.75);
}

.sa-aiflag {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    margin-right: 10px;
    color: rgba(0, 0, 0, 0.75);
}

.sa-override {
    font-size: 12px;
    margin-top: 8px;
    color: rgba(0, 0, 0, 0.65);
}

.sa-override-form {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed rgba(0, 0, 0, 0.12);
}

.sa-points-pill {
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 2px 10px;
    margin-left: 12px;
    white-space: nowrap;
}

.sa-rev {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.55);
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 3px;
    padding: 0 3px;
    margin-left: 4px;
    cursor: help;
}
</style>

<style>
/* Taller than Vuetify's 90% default; unscoped because the dialog is teleported. */
.sa-detail-dialog:not(.v-dialog--fullscreen) {
    max-height: 96vh !important;
    height: 96vh;
}

.sa-detail-dialog > .v-card {
    height: 100%;
}
</style>
